import React, { useEffect, useRef } from 'react'
import { useQueryClient, type Query } from '@tanstack/react-query'
import { filter } from 'rxjs/operators'
import api from 'api/api'
import { dispatchOrdinalStreamUpdate } from 'state/ordinalStreamUpdates'
import { useEventBus } from 'hooks/useEventBus'
import type { EventBusPayload } from 'services/eventBusService'
import { currentUserHasOrdinalsMarket } from 'components/panels/ordinalMarketFeature'
import {
    forgetOrdinalProvenance,
    recoverMountedOrdinalProvenance,
} from 'components/answers/ordinalProvenance'
import {
    dispatchOrdinalStateEvent,
    subscribeOrdinalState,
    subscribeOrdinalStateResync,
} from 'state/ordinalRealtime'
import {
    applyOrdinalStateToMarket,
    applyOrdinalStateToStreams,
    isViewerPurchase,
    mineStreamQueryKeys,
    prependOrdinalToMineStreams,
    mineStreamsHaveOrdinal,
    parseStreamFilter,
    isMineStreamFilter,
    type OrdinalStateEvent,
} from 'state/ordinalStatePatch'

const EVENT_BUS_TOPIC = 'ordinals.state'
let snapshotRequestId = 0

/**
 * Session-wide listener that turns ordinal state events into in-place cache
 * patches (docs/ordinal-state-events.md §5).
 *
 * Mounted once, beside OrdinalMintStatusListener, for the same reason: the
 * surfaces that need to change outlive any one of them. Stream rows unmount
 * while scrolling, a rail popover closes the moment a purchase starts, and the
 * ordinal may not be on screen at all when its sale lands.
 *
 * Nothing here invalidates a ranked stream. Composition changes only on an
 * explicit refresh; a live event may change what a row *says*, never whether it
 * is there.
 */
export default function OrdinalStateListener() {
    const queryClient = useQueryClient()
    // Insertion is the one non-idempotent reaction, so it is guarded per order.
    const handledPurchases = useRef(new Set<string>())

    useEffect(() => {
        const currentUserId = window.gon?.currentUser?.id ? String(window.gon.currentUser.id) : null
        const marketEnabled = currentUserHasOrdinalsMarket()

        const hydrating = new Map<string, OrdinalStateEvent[]>()
        let disposed = false

        const handle = (event: OrdinalStateEvent) => {
            if (!event?.ordinal_id) return
            hydrating.get(event.ordinal_id)?.push(event)

            applyOrdinalStateToStreams(queryClient, event, { currentUserId, marketEnabled })
            applyOrdinalStateToMarket(queryClient, event, { currentUserId })

            // Ownership, chain state, and sale history all render from the
            // provenance payload; drop the session cache so mounted artifact
            // headers and the details modal re-read it.
            if (event.origin) {
                forgetOrdinalProvenance(event.origin, event.revision)
                void queryClient.invalidateQueries({
                    queryKey: ['ordinalDescriptor', event.origin],
                })
            }

            if (isViewerPurchase(event)) void insertPurchase(event)
        }

        const insertPurchase = async (event: OrdinalStateEvent) => {
            const orderId = event.viewer?.settlement?.order_id || event.ordinal_id
            if (handledPurchases.current.has(orderId)) return

            const mineKeys = mineStreamQueryKeys(queryClient)
            if (!mineKeys.length) return
            if (mineStreamsHaveOrdinal(queryClient, event.ordinal_id)) return
            handledPurchases.current.add(orderId)
            hydrating.set(event.ordinal_id, [event])

            try {
                const res = await api.ordinalsMediaStream({
                    filter: { ordinals: true, ordinals_scope: 'mine' },
                    ids: [event.ordinal_id],
                    per_page: 1,
                })
                const item = res?.items?.find(row => String(row.id) === String(event.ordinal_id))
                if (!item) {
                    handledPurchases.current.delete(orderId)
                    return
                }
                prependOrdinalToMineStreams(queryClient, {
                    ...item,
                    ownership_status: item.ownership_status || 'unconfirmed',
                })
                // A confirmation may arrive before the hydration response.
                for (const pending of hydrating.get(event.ordinal_id) || []) {
                    applyOrdinalStateToStreams(queryClient, pending, {
                        currentUserId,
                        marketEnabled,
                    })
                }
            } catch (error) {
                console.error('>>> could not hydrate purchased ordinal', error)
                handledPurchases.current.delete(orderId)
                // Leave the list to repair itself on its next explicit refresh
                // rather than silently showing a wall without the purchase.
                mineKeys.forEach(queryKey => {
                    void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
                })
            } finally {
                hydrating.delete(event.ordinal_id)
            }
        }

        const needsRecovery = new WeakSet<Query>()
        const recoverQueries = (queries: Query[], provenanceIds: string[] = []) => {
            const ids = new Set(provenanceIds)
            let includePurchases = false
            queries.forEach(query => {
                const data = query.state.data as
                    | {
                          bsv_ordinal_id?: string
                          pages?: { items?: { id: string }[] }[]
                      }
                    | undefined
                if (query.queryKey[0] === 'ordinalsMediaStream') {
                    data?.pages?.forEach(page =>
                        page.items?.forEach(item => ids.add(String(item.id))),
                    )
                    includePurchases ||= isMineStreamFilter(parseStreamFilter(query.queryKey))
                } else if (query.queryKey[0] === 'ordinalMarket' && query.queryKey[1]) {
                    ids.add(String(query.queryKey[1]))
                } else if (query.queryKey[0] === 'ordinalDescriptor' && data?.bsv_ordinal_id) {
                    ids.add(data.bsv_ordinal_id)
                }
            })
            const recover = async (batch: string[], purchases = false) => {
                const response = await queryClient.fetchQuery({
                    // A second reconnect must not reuse a request started
                    // before that second network gap.
                    queryKey: ['ordinalStateSnapshot', ++snapshotRequestId],
                    queryFn: () => api.getOrdinalStates(batch, purchases),
                    retry: 2,
                    gcTime: 0,
                })
                if (disposed) return
                response.states.forEach(snapshot => {
                    // Also refresh mounted headers when a snapshot's revision
                    // equals the last socket frame. Invalidate once before
                    // dispatch so every header shares the same new request.
                    forgetOrdinalProvenance(snapshot.origin)
                    dispatchOrdinalStateEvent(snapshot)
                })
            }
            void (async () => {
                const visible = [...ids]
                for (let offset = 0; offset < visible.length && !disposed; offset += 100) {
                    await recover(visible.slice(offset, offset + 100))
                }
                // Keep capacity for purchases missing from an empty buyer wall
                // even when the visible batch already contains 100 records.
                if (includePurchases && currentUserId && !disposed) await recover([], true)
            })().catch(error => {
                queries.forEach(query => needsRecovery.add(query))
                console.error('>>> could not recover ordinal state', error)
            })
        }

        const unsubscribeState = subscribeOrdinalState(handle)
        const pendingActivations = new Set<Query>()
        const unsubscribeActivation = queryClient.getQueryCache().subscribe(event => {
            if (event.type !== 'observerAdded' || !needsRecovery.has(event.query)) return
            needsRecovery.delete(event.query)
            const alreadyScheduled = pendingActivations.size > 0
            pendingActivations.add(event.query)
            if (alreadyScheduled) return
            // Mounting a wall also mounts its rails and descriptors. Combine
            // that commit's activations so they share deduplicated ID batches.
            queueMicrotask(() => {
                const queries = [...pendingActivations]
                pendingActivations.clear()
                if (disposed) return
                const active = queries.filter(query => {
                    if (query.getObserversCount()) return true
                    needsRecovery.add(query)
                    return false
                })
                recoverQueries(active)
            })
        })
        const unsubscribeResync = subscribeOrdinalStateResync(() => {
            const active: Query[] = []
            queryClient
                .getQueryCache()
                .findAll()
                .forEach(query => {
                    if (
                        !['ordinalsMediaStream', 'ordinalMarket', 'ordinalDescriptor'].includes(
                            String(query.queryKey[0]),
                        )
                    )
                        return
                    if (query.getObserversCount()) {
                        needsRecovery.delete(query)
                        active.push(query)
                    } else {
                        // Remember the gap without fetching the session's history
                        // or authorizing a full-wall fetch on its next mount.
                        needsRecovery.add(query)
                    }
                })
            recoverQueries(active, recoverMountedOrdinalProvenance())
            dispatchOrdinalStreamUpdate({ ordinalId: 'reconnect' })
        })

        return () => {
            disposed = true
            unsubscribeState()
            unsubscribeResync()
            unsubscribeActivation()
        }
    }, [queryClient])

    // Guests get the public lane only. The EventBus service requires a signed-in
    // user, so its subscription lives in a child that simply is not rendered
    // for them.
    return window.gon?.currentUser?.id ? <OrdinalStateUserLane /> : null
}

/**
 * The per-user lane. It carries everything the shared lanes carry plus the
 * viewer block, so it is forwarded through the same dispatcher rather than
 * handled separately.
 */
function OrdinalStateUserLane() {
    const { getTopic$, isConnected } = useEventBus()

    useEffect(() => {
        if (!isConnected) return

        const subscription = getTopic$(EVENT_BUS_TOPIC)
            .pipe(filter((payload: EventBusPayload) => !!payload))
            .subscribe((payload: EventBusPayload) => {
                const event = (payload?.payload || payload) as unknown as OrdinalStateEvent
                if (event?.ordinal_id) dispatchOrdinalStateEvent(event)
            })

        return () => subscription.unsubscribe()
    }, [getTopic$, isConnected])

    return null
}
